import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Alert from "../../components/Alert/Alert";
import Pagination from "../../components/Pagination/Pagination";
import StatusBadge from "../../utilities/StatusBadge";
import FilterBar from "../../components/FilterBar/FilterBar";
import Loader from "../../components/Loader/Loader";
import AuthService from "../../services/Auth.service";
import ApiService from "../../services/Api.service";
import { Trash2, KeyRound, Pencil } from "lucide-react";
import ConfirmDeleteModal from "../ModelUI/ConfirmDeleteModal/ConfirmDeleteModal";
import ResetPasswordModal from "./ResetPasswordModal";
import AddAgentModal from "./AddAgentModal";

export default function UserList() {
    const [Loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [postsPerPage, setPostsPerPage] = useState(20);
    const [headers, setHeaders] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetTarget, setResetTarget] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [agentToEdit, setAgentToEdit] = useState(null);
    const [campaignOptions, setCampaignOptions] = useState([]);

    const [pagination, setPagination] = useState({
        page: 1,
        size: 20,
        total: 0,
        total_pages: 0,
    });
    const [alertValue, setAlertValue] = useState({
        show: false,
        message: "",
        variant: "success",
        position: "middle",
    });
    const [selectedRowIndex, setSelectedRowIndex] = useState(null);

    const getErrorMessage = (error, fallback) => {
        const detail = error?.response?.data?.detail;
        if (Array.isArray(detail)) {
            const msg = detail
                .map((item) => (typeof item?.msg === "string" ? item.msg : ""))
                .filter(Boolean)
                .join(", ");
            return msg || fallback;
        }
        if (typeof detail === "string") return detail;
        if (typeof error?.message === "string" && error.message) return error.message;
        return fallback;
    };

    const validationSchema = yup.object().shape({});

    const {
        handleSubmit,
        control,
        reset,
    } = useForm({
        resolver: yupResolver(validationSchema),
    });

    const fetchDetail = async (page = 1, size = postsPerPage) => {
        try {
            setLoading(true);
            const res = await AuthService.getAgentList({ page, size });
            if (res.status === 200 && res.data) {
                setTableData(res.data.data || []);
                setPagination(res.data.pagination || { page: 1, size, total: 0, total_pages: 0 });
                setHeaders(res.data.header || []);
                setSelectedRowIndex(null);
                setLoading(false);
            }
        } catch (error) {
            if (error?.response?.status === 404) {
                setTableData([]);
                setHeaders([]);
                setPagination({ page: 1, size, total: 0, total_pages: 0 });
            } else {
                console.error(error);
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetail();
        fetchCampaignOptions();
    }, []);

    const fetchCampaignOptions = async () => {
        try {
            const campaignRes = await ApiService.getCampaignOptions();
            if (campaignRes?.status === 200) {
                setCampaignOptions(campaignRes?.data?.data || []);
            }
        } catch (error) {
            console.error("Failed to load campaign options:", error);
            setCampaignOptions([]);
        }
    };

    const handelSubmit = async () => {
        fetchDetail(1, postsPerPage);
    };


    const handleClearFilters = () => {
        reset({});
        fetchDetail();
    };

    const handlePageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPostsPerPage(newSize);
        fetchDetail(1, newSize);
        setSelectedRowIndex(null);
    };

    const mistableTh = {
        backgroundColor:"transparent",
        color: "rgba(43, 58, 103, 1)",
        fontFamily: "Nunito",
        fontSize: "12px",
        fontWeight: 700,
        lineHeight: "16px",
        letterSpacing: "0.1em",
        textAlign: "center",
    };

    const tableTdStyles = {
        fontFamily: "Nunito",
        fontSize: "12px",
        fontWeight: "700",
        lineHeight: "16px",
        letterSpacing: "0em",
        textAlign: "center",
        color: "#232122",
    };

    const handleDeleteUser = async () => {
        if (!deleteTarget) return;
        try {
            const res = await AuthService.deleteAgent(deleteTarget.id);
            if (res.status === 200) {
                setAlertValue({
                    show: true,
                    message: `${deleteTarget.name || "Agent"} deleted successfully!`,
                    variant: "success",
                    position: "middle",
                });
                fetchDetail(pagination.page, postsPerPage); // refresh
            }
        } catch (error) {
            console.error(error);
            setAlertValue({
                show: true,
                message: "Failed to delete agent",
                variant: "danger",
                position: "middle",
            });
        } finally {
            setShowDeleteModal(false);
            setDeleteTarget(null);
        }
    };


    const handleResetPassword = async (payload) => {
        if (!resetTarget) return;
        try {
            const res = await AuthService.updateAgent({
                agent_id: resetTarget.id,
                name: payload.name,
                password: payload.password,
            });
            if (res.status === 200) {
                setAlertValue({
                    show: true,
                    message: "Password reset successfully!",
                    variant: "success",
                    position: "middle",
                });
            }
        } catch (error) {
            console.error(error);
            setAlertValue({
                show: true,
                message: getErrorMessage(error, "Failed to reset password"),
                variant: "danger",
                position: "middle",
            });
        } finally {
            setShowResetModal(false);
            setResetTarget(null);
        }
    };

    const handleAddAgent = async (data) => {
        try {
            let res;
            if (data.agent_id) {
                res = await AuthService.updateAgent(data);
            } else {
                res = await AuthService.createAgentLogin(data);
            }
            if (res.status === 200) {
                setAlertValue({
                    show: true,
                    message: res?.data?.message || (data.agent_id ? "Agent updated successfully!" : "Agent added successfully!"),
                    variant: "success",
                    position: "middle",
                });
                fetchDetail(1, postsPerPage);
                return true;
            }
        } catch (error) {
            setAlertValue({
                show: true,
                message: getErrorMessage(
                    error,
                    data.agent_id ? "Failed to update agent" : "Failed to add agent"
                ),
                variant: "danger",
                position: "middle",
            });
        }

        return false;
    };



    return (
        <> {alertValue.show && (
            <Alert
                variant={alertValue.variant}
                message={alertValue.message}
                dismissible={true}
                timeout={2500}
                position={alertValue.position}
                onClose={() => setAlertValue({ show: false, message: "", variant: "" })}
            />
        )}
            <div className="insights-container">
                <>
                    {Loading && (
                        <div className="loader-container">
                            <Loader />
                        </div>
                    )}

                    <div className="main-contanier">
                        <FilterBar
                            control={control}
                            handleSubmit={handleSubmit}
                            onSubmit={handelSubmit}
                            onClearFilters={handleClearFilters}
                            filters={[]}
                            title={"Agent List"}
                            totalCount={pagination.total || 0}
                            tableId="table-id"
                            isDownload={false}
                            isAdd ={true}
                            onAdd={() => {
                                setAgentToEdit(null);
                                setShowAddModal(true);
                            }}

                        />

                        <div className="table-container dailymis-table" style={{ height: "calc(100vh - 210px)" }}>
                            <table id="table-id" className="custom-table-container">
                                <thead
                                    className="table-head"
                                    style={{ position: "sticky", top: "0", zIndex: "1" }}
                                >

                                    <tr>
                                        <th style={mistableTh}>S.No</th>
                                        {headers.map((header, i) => (
                                            <th key={i} style={mistableTh}>
                                                {header.label}
                                            </th>
                                        ))}
                                        <th style={mistableTh}>Actions</th>
                                    </tr>


                                </thead>

                                <tbody>
                                    {tableData.length > 0 ? (
                                        tableData.map((item, index) => (
                                            <tr key={index} style={{ cursor: "pointer" }}>
                                                <td
                                                    style={{
                                                        ...tableTdStyles,
                                                        backgroundColor:
                                                            selectedRowIndex === index ? "#f9faff" : "transparent",
                                                    }}
                                                >
                                                    {index + 1}
                                                </td>
                                                {headers.map((header, i) => (
                                                    <td
                                                        key={i}
                                                        style={{
                                                            ...tableTdStyles,
                                                            backgroundColor:
                                                                selectedRowIndex === index ? "#f9faff" : "transparent",
                                                        }}
                                                    >
                                                        {header.value === "status" ? (
                                                            <StatusBadge status={item.status} />
                                                        ) : header.value === "disposition" ? (
                                                            <StatusBadge status={item.disposition} />
                                                        ) : (
                                                            item[header.value] ?? "--"
                                                        )}
                                                    </td>
                                                ))}
                                                <td style={tableTdStyles} >
                                                    <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
                                                        <Pencil
                                                            size={18}
                                                            color="#2B3A67"
                                                            onClick={() => {
                                                                setAgentToEdit(item);
                                                                setShowAddModal(true);
                                                            }}
                                                        />
                                                        <Trash2 size={18} color="#e63946"
                                                        onClick={() => {
                                                            setDeleteTarget(item);
                                                            setShowDeleteModal(true);
                                                        }}
                                                        /> 
                                                        <KeyRound size={18} 
                                                        color="#f4a261"  
                                                        onClick={() => {
                                                            setResetTarget(item);
                                                            setShowResetModal(true); 
                                                        }}/>
                                                    </div>
                                                </td>

                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={
                                                    headers.length + 1
                                                }
                                                style={tableTdStyles}
                                            >
                                                No data available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            pagination={pagination}
                            postsPerPage={postsPerPage}
                            handlePageSizeChange={handlePageSizeChange}
                            fetchDetail={fetchDetail}
                        />
                    </div>

                </>
            </div>
            {showDeleteModal && (
                <ConfirmDeleteModal
                    onClose={() => {
                        setShowDeleteModal(false);
                        setDeleteTarget(null);
                    }}
                    onConfirm={handleDeleteUser}
                    itemName={deleteTarget?.name || "this agent"}
                />
            )}
            {showResetModal && (
                <ResetPasswordModal
                    onClose={() => {
                        setShowResetModal(false);
                        setResetTarget(null);
                    }}
                    defaultName={resetTarget?.name || ""}
                    onReset={handleResetPassword}
                />
            )}
            {showAddModal && (
                <AddAgentModal
                    key={agentToEdit?.id || "new-agent"}
                    isOpen={showAddModal}
                    onClose={() => {
                        setShowAddModal(false);
                        setAgentToEdit(null);
                    }}
                    onSave={handleAddAgent}
                    campaignOptions={campaignOptions}
                    agentToEdit={agentToEdit}
                />
            )}

        </>
    );
}

